import { getFlattenedNodes, SummaryType } from '@ls/grouping-service';
import { createSelector } from "reselect";
import { uniqueByKey, memoize } from '@ls/js-utils';
import CalcType from 'state/Constants/CalcType';
import SelectionState from "state/selections/SelectionState";
import { selectSelectedAccountEntityId, selectSelectedBenchmarkEntityId, selectSelectedAccountEntity } from 'state/selections/selectors';
import { getEntitySummariesObject, getEntitySummariesObjectForBench } from 'state/util';
import scope from "./scope";

export const selectScan2CalcTypeOptions = state => state[scope].options;
export const selectData = (summaryType) => state => state[scope]['data'][summaryType];
export const selectFetchingData = (summaryType) => state => state[scope]['fetchingData'][summaryType];
export const selectFetchingVarSbsData = (summaryType) => (state) =>
  state[scope]["fetchingVarSbsData"][summaryType];
export const selectVarSbsData = (summaryType) => (state) =>
  state[scope]["varSbsData"][summaryType];
export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return state => state[scope]['definitions'][selectionState];
  }
  return createSelector(
    state => state[scope]['definitions'][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(({ complete }) => complete);
      return completeDefinitions.filter(uniqueByKey(completeDefinitions, 'key'));
    }
  );
});

export const selectVarSbsRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectVarSbsData(SummaryType.ABSOLUTE),
  selectVarSbsData(SummaryType.RELATIVE),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, accEntityId, bmkEntityId, absSbsData, relSbsData, benchId) => {
    const absoluteSbsNodes = getFlattenedNodes(absSbsData?.root) ?? [];
    const relativeSbsNodes = getFlattenedNodes(relSbsData?.root) ?? [];
    return [...absoluteSbsNodes, ...relativeSbsNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          const { scan2CalcType, percentiles, calcType  /*scaleType*/ } = definition;
          const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
          const benchValue = calcType === CalcType.Benchmark ? 0 : benchId?.benchmarkId;
          const isRelative = calcType === CalcType.Relative;
          let entitySummariesObject;
          if (benchValue !== null && benchValue !== undefined) {
            entitySummariesObject = getEntitySummariesObjectForBench(node, entityId, benchValue, isRelative);
          } else {
            entitySummariesObject = getEntitySummariesObject(node, entityId, isRelative) ?? {};
          }
          const scan2Var = entitySummariesObject?.var?.find(({ primaryKey }) => primaryKey.calcType === scan2CalcType && primaryKey.percentile === percentiles);
          row[`${definition.key} - scenario`] = scan2Var?.value;
        });
      return rowMap;
    }, {})
  });

export const selectRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectData(SummaryType.ABSOLUTE),
  selectData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, absData, relData, accEntityId, bmkEntityId, benchId) => {
    const absoluteNodes = getFlattenedNodes(absData?.root) ?? [];
    const relativeNodes = getFlattenedNodes(relData?.root) ?? [];
    return [...absoluteNodes, ...relativeNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          const { scan2CalcType, percentiles, calcType  /*scaleType*/ } = definition;
          const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
          const benchValue = calcType === CalcType.Benchmark ? 0 : benchId?.benchmarkId;
          const isRelative = calcType === CalcType.Relative;
          let entitySummariesObject;
          if (benchValue !== null && benchValue !== undefined) {
            entitySummariesObject = getEntitySummariesObjectForBench(node, entityId, benchValue, isRelative);
          } else {
            entitySummariesObject = getEntitySummariesObject(node, entityId, isRelative) ?? {};
          }
          const scan2Var = entitySummariesObject?.var?.find(({ primaryKey }) => primaryKey.calcType === scan2CalcType && primaryKey.percentile === percentiles);
          row[definition.key] = scan2Var?.value;
          //row[definition.key] = scan2Var?.values?.find(i => i.percentile === percentiles)?.value;
        });
      return rowMap;
    }, {});
  },
);

